import { sourceFrameThumbnailUrl } from '@src/utility/story';
import { getThumbnailTime } from '@src/utility/videoClip';
import { useMemo } from 'react';

import type { OtherVideo, Story } from '@src/types.ts/story';

/**
 * Scrim laid over the video frame in a caption-style preview.
 *
 * Caption previews render over a still from the footage being edited rather
 * than a stock backdrop, so the artwork underneath is bright and busy; this
 * keeps the caption text legible over it. Flat rather than a gradient because
 * the caption is centred — there is no single edge to weight.
 *
 * One value shared by both preview surfaces — the Captions panel thumbnail
 * (`CaptionsPreviewThumbnail`) and the style cards (`PresetPreview`) — so a
 * style looks the same in the panel and in the Replace Captions modal. They
 * carried 0.45 and 0.2 independently, which made the same preset read as two
 * different styles depending on where it was seen.
 */
export const CAPTION_PREVIEW_SCRIM = 'rgba(0, 0, 0, 0.45)';

/** Big enough for the largest caption preview (the enlarged hover card) at 2x. */
const CAPTION_PREVIEW_FRAME = { width: 640, height: 360 };

/** The subset of the store a caption preview needs to find its backdrop. */
type CaptionPreviewSource = {
  currentVideo?: OtherVideo;
  story?: Story;
};

/**
 * The frame a caption style is previewed over: a still of the **source
 * footage**, so the style being chosen is the only caption on it.
 *
 * Deliberately not the general `getThumbnailUrlAndAspectRatio` chain. That
 * prefers the Video record's `thumbnail` / `videoFilePrimary`, which are
 * artifacts of a published render — so once a clip had been published, this
 * preview showed captions burned into the frame *underneath* the caption style
 * being previewed, and the two competed for the same space.
 *
 * The frame is taken from within the clip's own range where there is one, so it
 * still shows the footage being worked on. Returns `undefined` when no source
 * frame resolves — callers fall through to the packaged
 * `/img/karaoke-preset-bg.png` backdrop.
 *
 * Shared so the panel thumbnail and the style cards cannot drift apart: they
 * held byte-identical copies of this derivation, one of which would inevitably
 * have been updated alone.
 */
export function useCaptionPreviewBackground(
  videoCreator: CaptionPreviewSource,
): string | undefined {
  const { currentVideo, story } = videoCreator;
  const clipJson = currentVideo?.clipJson;

  return useMemo(() => {
    // `getThumbnailTime` is the clip's own poster offset (start + 3s), so the
    // preview lands on the same frame the clip shows elsewhere.
    const frameTime = clipJson ? getThumbnailTime(clipJson) : undefined;
    return sourceFrameThumbnailUrl(story, frameTime, CAPTION_PREVIEW_FRAME);
  }, [story, clipJson]);
}
